import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import { OpenWith, PictureAsPdf } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { fontModules, fontFormats, smallStyle, registerFonts } from '../../utils/QuillConfig';

import { AlertDialog, ConfirmDialog, DialogTitleClose } from '../dialog';
import { FormInputText, FormInputAutoComplete } from '../form';
import CustomInputSelect from '../custom/select/CustomInputSelect';
import * as addressBookActions from '../../store/addressBook';
import * as gclientActions from '../../store/gclient';
import * as userActions from '../../store/user';
import * as subMatTemplateActions from '../../store/subMatTemplate';
import SubMatTemplateTocTree from './SubMatTemplateTocTree';
import SubMatTemplateMergedTocPreview from './SubMatTemplateMergedTocPreview';
import {
  SubMatTemplateTocDocFrame,
  SubMatTemplateTocPreviewTitle,
  useSubMatTocDocPreview,
} from './SubMatTemplateTocDocPreview';
import SubMatTemplateExtraAttachmentsList, {
  DEFAULT_EXTRA_SECTION_TITLE,
} from './SubMatTemplateExtraAttachmentsList';
import SubMatTemplateSendSection from './SubMatTemplateSendSection';
import SubMatTemplateDialogFooter from './SubMatTemplateDialogFooter';
import SubMatDialogFooterButton from './SubMatDialogFooterButton';
import CertifiedStampPrintPane from './CertifiedStampPrintPane';
import CertifiedStampPlacementDialog from './CertifiedStampPlacementDialog';
import { buildMergePreviewPayload, normalizeBodyHeaderPaging, normalizeTocNumbering } from './subMatTemplateTocOrder';
import { stampPlacementsToApi, stampPlacementsToUi } from './subMatStampPlacements';
import {
  STAMP_LAYOUT_COMPACT,
  STAMP_LAYOUT_FULL,
  normalizeStampLayout,
} from './certifiedStampSpec';

const newExtraId = () => `extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const STEP1_WORK_MIN_HEIGHT = 520;
const DIALOG_PAPER_SX = {
  width: { xs: 'calc(100vw - 32px)', md: 'min(1280px, calc(100vw - 64px))' },
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 64px)',
};

const normalizeSignOption = (value) => (
  value === 'NONE' ? 'NONE' : 'CERTIFIED_TRUE_COPY_AND_SIGN'
);

const defaultValues = {
  subject: '',
  html: '',
  supplierLabel: '제공사',
  supplier: '',
  recipientLabel: '제출처',
  recipient: '',
  siteLabel: '현장명',
  site: '',
  phrase: '',
  gclient: null,
  mailTo: [],
  mailCc: [],
};

/**
 * 템플릿 발송 — R11 + 별도 첨부(탐색기형) + SendMailDialog 메일 쓰기 동형.
 */
const SubMatTemplateSendDialog = ({ open, onClose, template, onSent }) => {
  const dispatch = useDispatch();
  const rowsAddressBooks = useSelector((s) => s.addressBook.addressBooks);
  const sessionUser = useSelector((s) => s.session.sessionUser);

  const [items, setItems] = useState([]);
  const [extras, setExtras] = useState([]);
  const [extraSectionTitle, setExtraSectionTitle] = useState(DEFAULT_EXTRA_SECTION_TITLE);
  const [tocNumbering, setTocNumbering] = useState('DECIMAL_HIERARCHY');
  const [bodyHeaderPaging, setBodyHeaderPaging] = useState(true);
  const [templateName, setTemplateName] = useState('');
  const [templateId, setTemplateId] = useState(null);
  /** 1: 목차·미리보기·별도첨부 / 2: 원본대조필·메일쓰기 */
  const [step, setStep] = useState(1);
  const tocDocPreview = useSubMatTocDocPreview(items, open);

  const [gclients, setGClients] = useState([]);
  const [mailTos, setMailTos] = useState([]);
  const [mailCcs, setMailCcs] = useState([]);
  const [mailCcInternals, setMailCcInternals] = useState([]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [emailOptionsTo, setEmailOptionsTo] = useState([]);
  const [emailOptionsCc, setEmailOptionsCc] = useState([]);
  const [emailOptionsCcInternal, setEmailOptionsCcInternal] = useState([]);
  const [gclientId, setGClientId] = useState('');
  const [signOption, setSignOption] = useState('CERTIFIED_TRUE_COPY_AND_SIGN');
  const [stampLayout, setStampLayout] = useState(STAMP_LAYOUT_FULL);
  const [emptyRecipient, setEmptyRecipient] = useState(false);
  const [emptySite, setEmptySite] = useState(false);
  const [URLThumbnail, setURLThumbnail] = useState('');
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [stampPlacementOpen, setStampPlacementOpen] = useState(false);
  /** FE 전용: `${sourceKey}::${pageIndex}` → { xPt, yPt } (PDF 좌하단 pt) */
  const [stampPlacements, setStampPlacements] = useState({});
  const [alertInfo, setAlertInfo] = useState({ open: false, titleAlert: '', messageAlert: '' });
  const [pdfEmptyConfirmOpen, setPdfEmptyConfirmOpen] = useState(false);
  const mailOptionsReadyRef = useRef(false);
  const mailOptionsLoadingRef = useRef(false);
  const thumbnailUrlRef = useRef('');

  const { handleSubmit, control, setValue, reset, watch, getValues } = useForm({
    defaultValues,
  });
  const htmlValue = watch('html');

  useEffect(() => {
    const Quill = ReactQuill.Quill;
    registerFonts(Quill);
  }, []);

  useEffect(() => {
    if (!open || !template) return;
    setItems((template.items || []).map((it) => ({ ...it })));
    setExtras([]);
    setExtraSectionTitle(DEFAULT_EXTRA_SECTION_TITLE);
    setTocNumbering(normalizeTocNumbering(template.tocNumbering));
    setBodyHeaderPaging(normalizeBodyHeaderPaging(template.bodyHeaderPaging));
    setStampPlacements(stampPlacementsToUi(template.stampPlacements, template.bodyHeaderPaging));
    setTemplateName(template.name || '');
    setTemplateId(template.id || null);
    setStep(1);
    setMailTos([]);
    setMailCcs([]);
    setMailCcInternals([]);
    setEmailOptionsTo([]);
    setEmailOptionsCc([]);
    setEmailOptionsCcInternal([]);
    setGClientId('');
    setSignOption(normalizeSignOption(template.signOption) || 'CERTIFIED_TRUE_COPY_AND_SIGN');
    setStampLayout(normalizeStampLayout(template.stampLayout));
    setEmptyRecipient(false);
    setEmptySite(false);
    setStampPlacementOpen(false);
    setPdfEmptyConfirmOpen(false);
    mailOptionsReadyRef.current = false;
    reset({
      ...defaultValues,
    });
  }, [open, template, reset]);

  // 거래선: comboData 경량 필드만 (풀 gclients 목록 회피)
  useEffect(() => {
    if (!open || !sessionUser) return;

    const phrases = sessionUser.phrases || {};
    setValue('supplierLabel', phrases.supplierLabel || '제공사');
    setValue('supplier', phrases.supplier || '');
    setValue('phrase', phrases.phrase || '');
    setValue('recipientLabel', '제출처');
    setValue('siteLabel', '현장명');

    const mailSettings = sessionUser.sessionUserLoginType === 'USER'
      ? sessionUser?.user?.mailSettings
      : sessionUser?.mailSettings;
    if (mailSettings?.footer?.useYn) {
      setValue('html', mailSettings.footer.message ?? '');
    } else {
      setValue('html', '');
    }

    let cancelled = false;
    (async () => {
      try {
        const list = await gclientActions.selectComboDataDirect(['id', 'name', 'email']);
        if (!cancelled) setGClients(list || []);
      } catch (_) {
        if (!cancelled) setGClients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionUser, setValue]);

  // 인감 썸네일: 인쇄안함 블러 미리보기에도 유지
  useEffect(() => {
    if (!open || !sessionUser?.signDocumentPath) {
      if (thumbnailUrlRef.current) {
        URL.revokeObjectURL(thumbnailUrlRef.current);
        thumbnailUrlRef.current = '';
      }
      setURLThumbnail('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await gclientActions.downloadDirect(sessionUser.signDocumentPath);
        const blob = await res.blob();
        if (cancelled) return;
        if (thumbnailUrlRef.current) URL.revokeObjectURL(thumbnailUrlRef.current);
        const url = URL.createObjectURL(blob);
        thumbnailUrlRef.current = url;
        setURLThumbnail(url);
      } catch (_) {
        if (!cancelled) {
          if (thumbnailUrlRef.current) URL.revokeObjectURL(thumbnailUrlRef.current);
          thumbnailUrlRef.current = '';
          setURLThumbnail('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionUser]);

  useEffect(() => () => {
    if (thumbnailUrlRef.current) {
      URL.revokeObjectURL(thumbnailUrlRef.current);
      thumbnailUrlRef.current = '';
    }
  }, []);

  // 주소록·내부참조: 메일 영역 포커스 시 1회 로드 (redux 주소록 있으면 재호출 생략)
  const ensureMailOptionsLoaded = useCallback(async () => {
    if (!open || !sessionUser) return;
    if (mailOptionsReadyRef.current || mailOptionsLoadingRef.current) return;
    mailOptionsLoadingRef.current = true;
    try {
      if (!rowsAddressBooks?.length) {
        await dispatch(addressBookActions.selectAll());
      }
      const { id, email, name } = sessionUser;
      try {
        const users = await userActions.selectAllByGClientDirect(id);
        const emailList = [];
        if (email) {
          emailList.push({
            id: `<${name}> <${email}> <${id}>`,
            name: `회사대표메일 <${email}>`,
            group: '거래선',
          });
        }
        (users || []).forEach((user) => {
          const { name: uName, userId, gclient } = user;
          emailList.push({
            id: `<${gclient.name}> <${userId}> <${gclient.id}>`,
            name: `${uName} <${userId}>`,
            group: '거래선',
          });
        });
        setEmailOptionsCcInternal(emailList);
      } catch (_) {
        setEmailOptionsCcInternal([]);
      }
      mailOptionsReadyRef.current = true;
    } finally {
      mailOptionsLoadingRef.current = false;
    }
  }, [open, sessionUser, rowsAddressBooks, dispatch]);

  useEffect(() => {
    if (!rowsAddressBooks?.length) {
      setAddressOptions([]);
      return;
    }
    const uniqueAddresses = Array.from(
      new Map(
        rowsAddressBooks.map((address) => [
          address.email,
          {
            id: `<${address.company}> <${address.email}> <${address.id}>`,
            name: `${address.name} <${address.email}>`,
            group: '주소록',
          },
        ]),
      ).values(),
    );
    setAddressOptions(uniqueAddresses);
  }, [rowsAddressBooks]);

  const includedTemplateCount = useMemo(
    () => items.filter((it) => it.included !== false).length,
    [items],
  );
  const includedExtras = useMemo(
    () => extras.filter((e) => e.included !== false),
    [extras],
  );
  const gclientOptions = useMemo(
    () => gclients.map((g) => ({ label: g.name, id: g.id })),
    [gclients],
  );

  const handleClose = () => {
    if (loading || merging) return;
    onClose();
  };

  const buildExportMailData = ({ tocPreviewOnly = false } = {}) => {
    const mergePayload = buildMergePreviewPayload(items, templateName, tocNumbering, bodyHeaderPaging);
    const phrases = {
      recipient: tocPreviewOnly ? '' : (getValues('recipient') || '').trim(),
      site: tocPreviewOnly ? '' : (getValues('site') || '').trim(),
      supplierLabel: getValues('supplierLabel'),
      supplier: getValues('supplier'),
      phrase: getValues('phrase'),
    };
    const extraAttachments = includedExtras.map((e) => ({
      title: e.title,
      included: true,
    }));
    return {
      mailData: {
        name: mergePayload.name,
        tocNumbering: mergePayload.tocNumbering,
        bodyHeaderPaging: mergePayload.bodyHeaderPaging,
        items: mergePayload.items,
        folderOrder: mergePayload.folderOrder,
        extraAttachments,
        extraSectionTitle: (extraSectionTitle || '').trim() || DEFAULT_EXTRA_SECTION_TITLE,
        // 부자재 합본은 우리회사 직인만 사용 (타사 직인 대체 옵션 없음)
        myCertified: true,
        // 1단계: 목차 확인용 — 원본대조필 미적용
        options: { signOption: tocPreviewOnly ? 'NONE' : signOption, stampLayout: normalizeStampLayout(stampLayout) },
        phrases,
        stampPlacements: tocPreviewOnly
          ? {}
          : stampPlacementsToApi(stampPlacements, extras, bodyHeaderPaging),
      },
      addedFiles: includedExtras.map((e) => e.file),
    };
  };

  const runPreviewMerge = async ({ tocPreviewOnly = false } = {}) => {
    setMerging(true);
    try {
      const response = await dispatch(
        subMatTemplateActions.previewSendMerge(buildExportMailData({ tocPreviewOnly })),
      );
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('PDF로 내보내지 못했습니다.');
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(templateName || '템플릿').replace(/[\\/:*?"<>|]/g, '_')}_합본.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setAlertInfo({
        open: true,
        titleAlert: '오류',
        messageAlert:
          e?.message
          || e?.serverResponse?.message
          || 'PDF로 내보내지 못했습니다.',
      });
    } finally {
      setMerging(false);
    }
  };

  const handlePreviewMerge = () => {
    if (includedTemplateCount === 0 && includedExtras.length === 0) {
      return;
    }
    // 1단계: 목차 확인용 — 제출처·현장 검증 생략
    if (step === 1) {
      runPreviewMerge({ tocPreviewOnly: true });
      return;
    }
    if (signOption === 'CERTIFIED_TRUE_COPY_AND_SIGN'
        && normalizeStampLayout(stampLayout) !== STAMP_LAYOUT_COMPACT) {
      const recipient = (getValues('recipient') || '').trim();
      const site = (getValues('site') || '').trim();
      if (!recipient || !site) {
        setEmptyRecipient(!recipient);
        setEmptySite(!site);
        setPdfEmptyConfirmOpen(true);
        return;
      }
    }
    runPreviewMerge();
  };

  const handleAddFiles = (files) => {
    const list = Array.isArray(files) ? files : [...(files || [])];
    const pdfOnly = list.filter((f) => /\.pdf$/i.test(f.name));
    if (pdfOnly.length !== list.length) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '별도 첨부는 PDF만 추가할 수 있습니다.',
      });
    }
    if (!pdfOnly.length) return;
    setExtras((prev) => [
      ...prev,
      ...pdfOnly.map((file) => ({
        id: newExtraId(),
        file,
        title: file.name.replace(/\.pdf$/i, ''),
        included: true,
      })),
    ]);
  };

  const handleChangeGClient = (_e, value) => {
    if (!value) {
      setValue('recipient', '');
      setGClientId('');
      setEmailOptionsTo([]);
      setEmailOptionsCc([]);
      setMailTos([]);
      setMailCcs([]);
      setValue('mailTo', []);
      setValue('mailCc', []);
      return;
    }
    const { id: selectedId, label } = value;
    // 제출처만 동기 반영 — Autocomplete 선택 완료 전에 To/Cc remount·비동기 로드가 끼면 첫 클릭이 씹힘
    setValue('recipient', label);
    setEmptyRecipient(false);
    setMailTos([]);
    setMailCcs([]);
    setValue('mailTo', []);
    setValue('mailCc', []);

    window.setTimeout(() => {
      setGClientId(selectedId);
      ensureMailOptionsLoaded();
      (async () => {
        try {
          const users = await userActions.selectAllByGClientDirect(selectedId);
          const gclient = gclients.find((g) => g.id === selectedId);
          const emailList = [];
          if (gclient?.email) {
            emailList.push({
              id: `<${gclient.name}> <${gclient.email}> <${gclient.id}>`,
              name: `회사대표메일 <${gclient.email}>`,
              group: '거래선',
            });
          }
          (users || []).forEach((user) => {
            const { name: uName, userId, gclient: gc } = user;
            emailList.push({
              id: `<${gc.name}> <${userId}> <${gc.id}>`,
              name: `${uName} <${userId}>`,
              group: '거래선',
            });
          });
          setEmailOptionsTo(emailList);
          setEmailOptionsCc(emailList);
        } catch (_) {
          setEmailOptionsTo([]);
          setEmailOptionsCc([]);
        }
      })();
    }, 0);
  };

  const handleChangeRecipient = (e) => {
    const { value } = e.target;
    setEmptyRecipient(!value);
    setValue('recipient', value);
  };

  const handleChangeSite = (e) => {
    const { value } = e.target;
    setEmptySite(!value);
    setValue('site', value);
  };

  const handleNextStep = () => {
    if (includedTemplateCount === 0 && includedExtras.length === 0) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '발송에 포함할 목차 항목 또는 별도 첨부가 없습니다.',
      });
      return;
    }
    setStep(2);
  };

  const handlePrevStep = () => {
    setStep(1);
  };

  const onSubmit = async (form) => {
    const {
      subject,
      html,
      supplierLabel,
      supplier,
      recipient,
      site,
      phrase,
    } = form;

    if (!mailTos?.length) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '받는 사람 메일 주소를 입력해 주세요.',
      });
      return;
    }
    if (includedTemplateCount === 0 && includedExtras.length === 0) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '발송에 포함할 목차 항목 또는 별도 첨부가 없습니다.',
      });
      return;
    }
    if (signOption === 'CERTIFIED_TRUE_COPY_AND_SIGN'
        && normalizeStampLayout(stampLayout) !== STAMP_LAYOUT_COMPACT) {
      if (!recipient?.trim() || !site?.trim()) {
        setEmptyRecipient(!recipient?.trim());
        setEmptySite(!site?.trim());
        setAlertInfo({
          open: true,
          titleAlert: '안내',
          messageAlert: '원본대조필 사용 시 제출처·현장명을 입력해 주세요.',
        });
        return;
      }
    }

    const mergePayload = buildMergePreviewPayload(items, templateName, tocNumbering, bodyHeaderPaging);
    const firstTo = mailTos[0];
    const gclientName = firstTo?.id?.match?.(/<([^>]+)>/)?.[1]
      || firstTo?.name?.replace(/<[^>]+>/, '').trim()
      || '';

    const filesToUpload = includedExtras.map((e) => e.file);
    const extraAttachments = includedExtras.map((e) => ({
      title: e.title,
      included: true,
    }));

    setLoading(true);
    try {
      const data = await dispatch(subMatTemplateActions.send({
        mailData: {
          templateId,
          name: mergePayload.name,
          tocNumbering: mergePayload.tocNumbering,
          bodyHeaderPaging: mergePayload.bodyHeaderPaging,
          items: mergePayload.items,
          folderOrder: mergePayload.folderOrder,
          extraAttachments,
          extraSectionTitle: (extraSectionTitle || '').trim() || DEFAULT_EXTRA_SECTION_TITLE,
          mailTo: mailTos,
          mailCc: mailCcs || [],
          mailCcInternal: mailCcInternals || [],
          subject,
          html,
          sendReplace: false,
          myCertified: true,
          gclientTo: { name: gclientName },
          options: { signOption, stampLayout: normalizeStampLayout(stampLayout) },
          phrases: {
            recipient: recipient?.trim() || '',
            site: site?.trim() || '',
            supplierLabel,
            supplier,
            phrase,
          },
          stampPlacements: stampPlacementsToApi(stampPlacements, extras, bodyHeaderPaging),
        },
        addedFiles: filesToUpload,
      }));
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '메일이 전송되었습니다.',
        handleClose: () => {
          if (onSent) onSent(data?.gsendG04docu);
          onClose();
        },
      });
    } catch (e) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: e?.message || '발송에 실패했습니다.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        aria-labelledby="submat-send-dialog-title"
      >
        <DialogTitleClose
          id="submat-send-dialog-title"
          onClose={handleClose}
          sx={{ m: 0, px: 2, pt: 2, pb: 1.5 }}
        >
          부자재 자재승인서 메일전송
        </DialogTitleClose>
        <DialogContent
          dividers
          sx={{
            px: 2,
            // theme.js `.MuiDialogContent-root { padding-top: 10px !important }` 덮어씀
            pt: '16px !important',
            pb: '16px !important',
          }}
        >
          {step === 1 && (
            <Box
              sx={{
                minHeight: STEP1_WORK_MIN_HEIGHT,
                height: { md: `min(70vh, ${STEP1_WORK_MIN_HEIGHT + 80}px)` },
                overflow: 'hidden',
              }}
            >
              <Grid container spacing={2} alignItems="stretch" sx={{ height: '100%' }}>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    minHeight: 0,
                    height: { md: '100%' },
                  }}
                >
                  <SubMatTemplateSendSection
                    title="목차 구성"
                    sx={{ flex: 1, minHeight: 0 }}
                    contentSx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                  >
                    <SubMatTemplateTocTree
                      items={items}
                      onChange={setItems}
                      sendMode
                      bare
                      onPreview={tocDocPreview.previewItem}
                      previewLoadingId={tocDocPreview.loadingId}
                    />
                  </SubMatTemplateSendSection>
                  <SubMatTemplateExtraAttachmentsList
                    extras={extras}
                    onChange={setExtras}
                    onAddFiles={handleAddFiles}
                    sectionTitle={extraSectionTitle}
                    onSectionTitleChange={setExtraSectionTitle}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: { md: '100%' },
                  }}
                >
                  <SubMatTemplateSendSection
                    title={(
                      <SubMatTemplateTocPreviewTitle
                        pane={tocDocPreview.pane}
                        onPane={tocDocPreview.setPane}
                        docTitle={tocDocPreview.doc?.title}
                        onCloseDoc={tocDocPreview.closeDoc}
                      />
                    )}
                    sx={{ flex: 1, minHeight: 0, height: '100%' }}
                    contentSx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      bgcolor: tocDocPreview.pane === 'doc' ? 'background.paper' : 'grey.700',
                      p: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    {tocDocPreview.pane === 'doc' ? (
                      <SubMatTemplateTocDocFrame
                        url={tocDocPreview.doc?.url}
                        error={tocDocPreview.error}
                        loading={Boolean(tocDocPreview.loadingId)}
                      />
                    ) : (
                      <SubMatTemplateMergedTocPreview
                        fillHeight
                        bare
                        documentLook
                        items={items}
                        tocNumbering={tocNumbering}
                        extraAttachments={extras}
                        extraSectionTitle={extraSectionTitle}
                      />
                    )}
                  </SubMatTemplateSendSection>
                </Grid>
              </Grid>
            </Box>
          )}

          {step === 2 && (
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              sx={{
                minHeight: { md: 420 },
              }}
            >
              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <SubMatTemplateSendSection
                  title="원본대조필"
                  sx={{ flex: 1, width: '100%' }}
                  contentSx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: 0,
                  }}
                >
                  <CertifiedStampPrintPane
                    signOption={signOption}
                    stampLayout={stampLayout}
                    onChangeSignOption={setSignOption}
                    onChangeStampLayout={setStampLayout}
                    control={control}
                    signThumbnailUrl={URLThumbnail}
                    emptyRecipient={emptyRecipient}
                    emptySite={emptySite}
                    onChangeRecipient={handleChangeRecipient}
                    onChangeSite={handleChangeSite}
                    captionCompact="원본대조필과 인감만 인쇄합니다."
                    captionFull="제출처·현장명은 메일 전송 시 필수입니다."
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenWith />}
                      onClick={() => setStampPlacementOpen(true)}
                      sx={{ mt: 1 }}
                    >
                      문서에서 위치 지정
                    </Button>
                    {Object.keys(stampPlacements).length > 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        위치 {Object.keys(stampPlacements).length}곳 지정됨
                      </Typography>
                    ) : null}
                  </CertifiedStampPrintPane>
                </SubMatTemplateSendSection>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <SubMatTemplateSendSection
                  title="메일 쓰기"
                  sx={{ flex: 1, width: '100%', overflow: 'visible' }}
                  contentSx={{ p: 2, overflow: 'visible' }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormInputAutoComplete
                        key={`gclient-${open}-${templateId || ''}`}
                        name="gclient"
                        control={control}
                        label="거래선명"
                        options={gclientOptions}
                        setValue={setValue}
                        defaultValue={null}
                        onCustomChange={handleChangeGClient}
                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.label || ''))}
                        ListboxProps={{
                          onMouseDown: (e) => e.preventDefault(),
                        }}
                        componentsProps={{
                          popper: { disablePortal: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} onFocus={() => { ensureMailOptionsLoaded(); }}>
                      <CustomInputSelect
                        key={`to-${open}-${templateId || ''}-${gclientId}`}
                        defaultOptions={emailOptionsTo}
                        addressOptions={addressOptions}
                        label="받는 사람"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailTos(value);
                          setValue('mailTo', value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} onFocus={() => { ensureMailOptionsLoaded(); }}>
                      <CustomInputSelect
                        key={`cc-${open}-${templateId || ''}-${gclientId}`}
                        defaultOptions={emailOptionsCc}
                        addressOptions={addressOptions}
                        label="참조"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailCcs(value);
                          setValue('mailCc', value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} onFocus={() => { ensureMailOptionsLoaded(); }}>
                      <CustomInputSelect
                        key={`cci-${open}-${templateId || ''}`}
                        defaultOptions={emailOptionsCcInternal}
                        addressOptions={[]}
                        label="내부참조"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailCcInternals(value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        name="subject"
                        control={control}
                        label="제목"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={smallStyle}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            marginBottom: 1,
                            fontWeight: 500,
                            color: 'rgba(0, 0, 0, 0.87)',
                          }}
                        />
                        <ReactQuill
                          value={htmlValue || ''}
                          onChange={(content) => setValue('html', content)}
                          modules={fontModules}
                          formats={fontFormats}
                          placeholder="메시지를 입력하세요..."
                          theme="snow"
                          style={{
                            height: '150px',
                            marginBottom: '20px',
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </SubMatTemplateSendSection>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <SubMatTemplateDialogFooter
          cancelDisabled={loading || merging}
          onCancel={handleClose}
          pdfButton={(
            <SubMatDialogFooterButton
              variant="outlined"
              startIcon={<PictureAsPdf />}
              loading={merging}
              disabled={loading}
              onClick={handlePreviewMerge}
            >
              PDF 보기
            </SubMatDialogFooterButton>
          )}
          beforePrimary={step === 2 ? (
            <SubMatDialogFooterButton
              variant="outlined"
              onClick={handlePrevStep}
              disabled={loading || merging}
            >
              이전
            </SubMatDialogFooterButton>
          ) : null}
          primaryButton={step === 1 ? (
            <SubMatDialogFooterButton
              variant="contained"
              onClick={handleNextStep}
              disabled={loading || merging}
            >
              다음
            </SubMatDialogFooterButton>
          ) : (
            <SubMatDialogFooterButton
              variant="contained"
              loading={loading}
              disabled={merging}
              onClick={handleSubmit(onSubmit)}
            >
              보내기
            </SubMatDialogFooterButton>
          )}
        />
      </Dialog>

      <CertifiedStampPlacementDialog
        open={stampPlacementOpen}
        onClose={() => setStampPlacementOpen(false)}
        items={items}
        extras={extras}
        extraSectionTitle={extraSectionTitle}
        tocNumbering={tocNumbering}
        bodyHeaderPaging={bodyHeaderPaging}
        headerLeft={(gclients.find((c) => c.id === gclientId) || {}).name}
        signThumbnailUrl={URLThumbnail}
        placements={stampPlacements}
        onChangePlacements={setStampPlacements}
        stampLayout={stampLayout}
      />

      <ConfirmDialog
        title="안내"
        open={pdfEmptyConfirmOpen}
        setOpen={setPdfEmptyConfirmOpen}
        onConfirm={() => runPreviewMerge()}
        onCancel={() => {}}
        confirmLabel="확인"
        cancelLabel="취소"
      >
        제출처·현장명이 비어 있습니다. 이 PDF를 그대로 외부에 전달하면 원본대조필 정보가 불완전할 수 있습니다. 계속할까요?
      </ConfirmDialog>

      <AlertDialog
        alertInfo={alertInfo}
        setAlertInfo={setAlertInfo}
      />
    </>
  );
};

export default SubMatTemplateSendDialog;
